// Auth contracts shared by both strategies (BRIEF owner addition B):
//   'authentik' — online/cloud: Authentik headless flow, business-web, phone + PIN
//   'local'     — intranet node: POST /api/erp/auth/login (erp-core local_users)
// Same session shape either way; the rest of the app never knows which one ran.

export type AuthStrategyName = 'authentik' | 'local';

export interface SessionUser {
  /** Subject / user id when the strategy knows it. */
  id?: string;
  name: string;
  /** E.164 phone (+9647…). Kept so the lock screen can re-authenticate. */
  phone: string;
  initial: string;
}

export interface SessionTokens {
  accessToken: string;
  refreshToken?: string;
  /** Access-token lifetime in seconds (0 = unknown). */
  expiresIn?: number;
}

/** A business this person belongs to, as erp-core's local login returns it. */
export interface SessionMembership {
  business_id: string;
  business_name: string;
  role: 'owner' | 'manager' | 'staff';
}

export interface LoginResult {
  tokens: SessionTokens;
  user: SessionUser;
  /**
   * Node ('local'): the memberships from POST /v1/erp/auth/login|unlock — the
   * business switcher's source on a node. Undefined for strategies that do
   * not return them (authentik: cloud memberships come from profile).
   */
  memberships?: SessionMembership[];
  /** Node ('local'): `idle_lock_seconds` — the server's idle lock; the client locks at the same point. */
  idleLockSeconds?: number;
}

export interface AuthStrategy {
  readonly name: AuthStrategyName;
  /** Phone (any Iraqi format, any digits) + 6-digit PIN → a session. Throws AuthError. */
  login(phone: string, pin: string): Promise<LoginResult>;
  /** Silent renewal; null when the strategy has nothing to renew with. */
  refresh(refreshToken: string | null): Promise<LoginResult | null>;
  /** End the server-side session (best effort; never throws). */
  logout(): Promise<void>;
  /**
   * Optional: resume an idle-locked session with the PIN (same session, fresh
   * auth time). Strategies without it re-run login() for the stored phone.
   * Throws AuthError; 'session_ended' means the session is gone → full login.
   */
  unlock?(pin: string, accessToken: string): Promise<LoginResult>;
}

export type AuthErrorCode =
  | 'invalid_phone'
  | 'invalid_credentials'
  | 'locked'
  | 'denied'
  | 'network'
  | 'unexpected'
  | 'session_ended'
  // Enrollment (web onboarding, business-enrollment flow):
  | 'already_registered'
  | 'invalid_code'
  | 'code_expired'
  | 'weak_pin'
  | 'flow_expired';

export class AuthError extends Error {
  readonly code: AuthErrorCode;
  /** Seconds until the account lockout ends (code 'locked'). */
  readonly retryAfterSeconds?: number;
  /** Tries left before the lockout, when the server says (code 'invalid_code' from the code exchange). */
  readonly attemptsLeft?: number;
  constructor(code: AuthErrorCode, message: string, retryAfterSeconds?: number, attemptsLeft?: number) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
    this.attemptsLeft = attemptsLeft;
  }
}

/** Arabic copy shared with the business app (feature_auth). */
export const AUTH_COPY = {
  invalidPhone: 'رقم الهاتف غير صحيح — يجب أن يبدأ بـ 07',
  invalidPin: 'الرمز السري غير صحيح',
  denied: 'تم رفض الدخول',
  network: 'لم تنجح المحاولة.',
  unexpected: 'تعذّر إكمال تسجيل الدخول',
  sessionEnded: 'انتهت الجلسة، سجّل الدخول مجدداً',
  // Enrollment. alreadyRegistered: the owner's sentence (web onboarding brief);
  // invalidCode: feature_auth ValidationException; pinWeak: board 04
  // auth/pin-set--weak; flowExpired: feature_auth «انتهت الجلسة. أعد المحاولة.».
  alreadyRegistered: 'عندك حساب، سجّل الدخول',
  invalidCode: 'رمز التحقق غير صحيح',
  // ihjizli-otp exchange 410 `expired`: the sent code's window ended (or none is on record).
  codeExpired: 'انتهت صلاحية الرمز. اطلب رمزاً جديداً.',
  pinWeak: 'سهل التخمين. اختر رمزاً آخر.',
  flowExpired: 'انتهت الجلسة. أعد المحاولة.',
} as const;

/** Parses the lockout contract `LOCKED:<seconds>` (ADR-0018 #7). */
export function parseLocked(message: unknown): number | null {
  if (typeof message !== 'string') return null;
  const m = /^LOCKED:(\d+)$/.exec(message.trim());
  return m ? Number(m[1]) : null;
}
